import React, { useState, useEffect } from 'react';

interface PopoverProps {
  initialStartTime: string;
  initialEndTime: string;
  onApply: (start: string, end: string, hours: string) => void;
  onClose: () => void;
}

export const ExtensionTimeEntryPopover: React.FC<PopoverProps> = ({
  initialStartTime,
  initialEndTime,
  onApply,
  onClose,
}) => {
  const parseTo12Hour = (timeStr: string) => {
    if (!timeStr || !timeStr.includes(':')) return { time: '09:00', ampm: 'AM' };
    const [hStr, mStr] = timeStr.split(':');
    let h = parseInt(hStr, 10);
    const m = mStr || '00';
    let ampm = 'AM';

    if (h >= 12) {
      ampm = 'PM';
      if (h > 12) h -= 12;
    }
    if (h === 0) h = 12;

    return {
      time: `${String(h).padStart(2, '0')}:${m}`,
      ampm,
    };
  };

  const initialStart = parseTo12Hour(initialStartTime || '09:00');
  const initialEnd = parseTo12Hour(initialEndTime || '17:00');

  const [startTimeInput, setStartTimeInput] = useState<string>(initialStart.time);
  const [startAmpm, setStartAmpm] = useState<string>(initialStart.ampm);

  const [endTimeInput, setEndTimeInput] = useState<string>(initialEnd.time);
  const [endAmpm, setEndAmpm] = useState<string>(initialEnd.ampm);

  const [calculatedHours, setCalculatedHours] = useState<string>('00:00');
  const [popoverError, setPopoverError] = useState<string>('');

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: string) => void) => {
    let val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length > 4) val = val.slice(0, 4);

    let formatted = val;
    if (val.length >= 3) {
      formatted = val.slice(0, 2) + ':' + val.slice(2);
    }

    const parts = formatted.split(':');
    if (parts[0]) {
      let h = parseInt(parts[0], 10);
      if (h > 12) parts[0] = '12';
      if (parts[0].length === 2 && h === 0) parts[0] = '01';
    }
    if (parts[1]) {
      let m = parseInt(parts[1], 10);
      if (m > 59) parts[1] = '59';
    }

    setter(parts.join(':'));
  };

  const convertToTotalMinutes = (timeStr: string, ampm: string) => {
    if (!timeStr || !timeStr.includes(':')) return 0;
    const [hStr, mStr] = timeStr.split(':');
    let hoursNum = parseInt(hStr, 10) || 0;
    const minsNum = parseInt(mStr, 10) || 0;

    if (ampm === 'PM' && hoursNum < 12) hoursNum += 12;
    if (ampm === 'AM' && hoursNum === 12) hoursNum = 0;

    return hoursNum * 60 + minsNum;
  };

  useEffect(() => {
    const startTotal = convertToTotalMinutes(startTimeInput, startAmpm);
    const endTotal = convertToTotalMinutes(endTimeInput, endAmpm);

    let diffMins = endTotal - startTotal;
    if (diffMins < 0) {
      diffMins += 24 * 60;
    }

    if (diffMins < 1) {
      setCalculatedHours('00:00');
      setPopoverError('End time must be after start time.');
    } else {
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      setCalculatedHours(`${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`);
      setPopoverError('');
    }
  }, [startTimeInput, startAmpm, endTimeInput, endAmpm]);

  const formatTo24Hour = (timeStr: string, ampm: string) => {
    if (!timeStr || !timeStr.includes(':')) return '00:00';
    const [hStr, mStr] = timeStr.split(':');
    let hoursNum = parseInt(hStr, 10) || 0;
    const m = mStr || '00';

    if (ampm === 'PM' && hoursNum < 12) hoursNum += 12;
    if (ampm === 'AM' && hoursNum === 12) hoursNum = 0;

    return `${String(hoursNum).padStart(2, '0')}:${m}`;
  };

  const handleApply = () => {
    if (!popoverError && calculatedHours !== '00:00') {
      const formattedStart = formatTo24Hour(startTimeInput, startAmpm);
      const formattedEnd = formatTo24Hour(endTimeInput, endAmpm);
      onApply(formattedStart, formattedEnd, calculatedHours);
      onClose();
    }
  };

  return (
    <div
      className="card shadow-lg position-absolute border bg-white p-3 rounded-4"
      style={{
        top: '100%',
        left: 0,
        right: 0,
        marginTop: '6px',
        width: '350px',
        zIndex: 1050,
        borderColor: '#E4E6EF',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom border-gray-200">
        <span className="fw-bold fs-7 text-dark" style={{ letterSpacing: '-0.2px' }}>
          Set Time Range
        </span>
        <button
          type="button"
          className="btn-close"
          style={{ fontSize: '10px' }}
          onClick={onClose}
        ></button>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        {/* Start Time Field */}
        <div className="flex-grow-1">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Start Time</label>
          <div className="input-group input-group-sm">
            <input
              type="text"
              placeholder="HH:MM"
              maxLength={5}
              className="form-control fw-semibold text-dark text-center px-1"
              style={{ height: '36px', fontSize: '12px', backgroundColor: '#F9FAFB', borderColor: '#E5E7EB', width: '60px' }}
              value={startTimeInput}
              onChange={(e) => handleTimeChange(e, setStartTimeInput)}
            />
            <select
              className="form-select fw-bold text-primary px-2"
              style={{ height: '36px', fontSize: '11px', backgroundColor: '#F4F6F9', borderColor: '#E5E7EB', width: '65px' }}
              value={startAmpm}
              onChange={(e) => setStartAmpm(e.target.value)}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>

        <span className="text-muted fw-bold pt-4">to</span>

        {/* End Time Field */}
        <div className="flex-grow-1">
          <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">End Time</label>
          <div className="input-group input-group-sm">
            <input
              type="text"
              placeholder="HH:MM"
              maxLength={5}
              className="form-control fw-semibold text-dark text-center px-1"
              style={{ height: '36px', fontSize: '12px', backgroundColor: '#F9FAFB', borderColor: '#E5E7EB', width: '60px' }}
              value={endTimeInput}
              onChange={(e) => handleTimeChange(e, setEndTimeInput)}
            />
            <select
              className="form-select fw-bold text-primary px-2"
              style={{ height: '36px', fontSize: '11px', backgroundColor: '#F4F6F9', borderColor: '#E5E7EB', width: '65px' }}
              value={endAmpm}
              onChange={(e) => setEndAmpm(e.target.value)}
            >
              <option value="AM">AM</option>
              <option value="PM">PM</option>
            </select>
          </div>
        </div>
      </div>

      {popoverError && (
        <div className="text-danger fs-9 mb-2 fw-semibold text-center">
          {popoverError}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center px-2 py-1.5 mb-2 rounded-3" style={{ backgroundColor: '#F4F6F9' }}>
        <span className="fs-8 text-gray-600 fw-semibold">Calculated:</span>
        <span className="fw-bold fs-8 text-primary">{calculatedHours} hrs</span>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-sm btn-light fs-8 py-1 px-3"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={Boolean(popoverError) || calculatedHours === '00:00'}
          className="btn btn-sm btn-primary fs-8 py-1 px-3"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default ExtensionTimeEntryPopover;

// import React, { useState, useEffect } from 'react';

// interface PopoverProps {
//   initialStartTime: string;
//   initialEndTime: string;
//   onApply: (start: string, end: string, hours: string) => void;
//   onClose: () => void;
// }

// export const ExtensionTimeEntryPopover: React.FC<PopoverProps> = ({
//   initialStartTime,
//   initialEndTime,
//   onApply,
//   onClose,
// }) => {
//   const [startTime, setStartTime] = useState<string>(initialStartTime || '');
//   const [endTime, setEndTime] = useState<string>(initialEndTime || '');
//   const [calculatedHours, setCalculatedHours] = useState<string>('00:00');
//   const [popoverError, setPopoverError] = useState<string>('');

//   useEffect(() => {
//     if (startTime && endTime) {
//       const [startH, startM] = startTime.split(':').map(Number);
//       const [endH, endM] = endTime.split(':').map(Number);

//       let startTotal = startH * 60 + startM;
//       let endTotal = endH * 60 + endM;

//       if (endTotal < startTotal) {
//         endTotal += 24 * 60; 
//       }

//       const diffMins = endTotal - startTotal;

//       if (diffMins < 1) {
//         setCalculatedHours('00:00');
//         setPopoverError('Start and End time must differ by at least 1 minute.');
//       } else {
//         const hours = Math.floor(diffMins / 60);
//         const mins = diffMins % 60;
//         const formatted = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
//         setCalculatedHours(formatted);
//         setPopoverError('');
//       }
//     } else {
//       setCalculatedHours('00:00');
//       setPopoverError('Please select both Start and End time.');
//     }
//   }, [startTime, endTime]);

//   const isValidSelection = calculatedHours !== '00:00' && !popoverError;

//   return (
//     <div
//       className="card shadow-lg position-absolute border bg-white p-3 z-index-3"
//       style={{
//         top: '100%',
//         left: 0,
//         right: 0,
//         marginTop: '6px',
//         width: '320px',
//         zIndex: 1050,
//       }}
//     >
//       <style>{`
//         input[type="time"]::-webkit-calendar-picker-indicator {
//           cursor: pointer;
//           opacity: 0.6;
//         }
//         input[type="time"]::-webkit-clear-button {
//           display: none;
//         }
//       `}</style>

//       <div className="d-flex justify-content-between align-items-center mb-3">
//         <span className="fw-bold fs-7 text-gray-800">Select Start & End Time</span>
//         <button
//           type="button"
//           className="btn-close"
//           style={{ fontSize: '10px' }}
//           onClick={onClose}
//         ></button>
//       </div>

//       <div className="row g-2 mb-2">
//   <div className="col-6">
//     <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">Start Time</label>
//     <input
//       type="time"
//       className="form-control form-control-sm px-2"
//       style={{ height: '30px', fontSize: '11px' }}
//       value={startTime}
//       onChange={(e) => setStartTime(e.target.value)}
//     />
//   </div>
//   <div className="col-6">
//     <label className="form-label fs-8 fw-semibold text-gray-600 mb-1">End Time</label>
//     <input
//       type="time"
//       className="form-control form-control-sm px-2"
//       style={{ height: '30px', fontSize: '11px' }}
//       value={endTime}
//       onChange={(e) => setEndTime(e.target.value)}
//     />
//   </div>
// </div>

//       {popoverError && (
//         <div className="text-danger fs-9 mb-2 fw-semibold">
//           {popoverError}
//         </div>
//       )}

//       <div className="d-flex justify-content-end align-items-center gap-1 mb-3">
//         <span className="fs-9 text-gray-600">Calculated:</span>
//         <span className="fw-bold fs-8 text-primary">{calculatedHours} hrs</span>
//       </div>

//       <div className="d-flex justify-content-end gap-2">
//         <button
//           type="button"
//           className="btn btn-sm btn-light fs-8 py-1 px-3"
//           onClick={onClose}
//         >
//           Cancel
//         </button>
//         <button
//           type="button"
//           disabled={!isValidSelection}
//           className="btn btn-sm btn-primary fs-8 py-1 px-3"
//           onClick={() => {
//             if (isValidSelection) {
//               onApply(startTime, endTime, calculatedHours);
//               onClose();
//             }
//           }}
//         >
//           Apply
//         </button>
//       </div>
//     </div>
//   );
// };

// export default ExtensionTimeEntryPopover;